import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Send, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  'Lorry Crane', 'Tailgate Lorry', 'Trailer', 'Prime Mover',
  'Manpower', 'Warehouse', 'Equipment Rental', 'Transport', 'Other'
];

const EMPTY_JOB = {
  job_date: '',
  job_time: '',
  pickup_location: '',
  dropoff_location: '',
  service: '',
  cargo: '',
  weight: '',
  customer_ref: '',
  instructions: '',
};

const buildJobMessage = (sup, j) => {
  const today = new Date().toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
  const lines = [
    `*NJK LOGISTICS — JOB ASSIGNMENT*`,
    ``,
    `Hi ${sup?.contact_person || sup?.company_name || 'there'},`,
    ``,
    `We'd like to assign the following job:`,
    ``,
    `*Date:* ${j.job_date || 'TBC'}${j.job_time ? `    *Time:* ${j.job_time}` : ''}`,
    j.service ? `*Service:* ${j.service}` : '',
    j.pickup_location ? `*Pickup:* ${j.pickup_location}` : '',
    j.dropoff_location ? `*Drop-off:* ${j.dropoff_location}` : '',
    j.cargo ? `*Cargo / Item:* ${j.cargo}` : '',
    j.weight ? `*Weight / Qty:* ${j.weight}` : '',
    j.customer_ref ? `*Customer / Ref:* ${j.customer_ref}` : '',
    j.instructions ? `\n*Instructions:*\n${j.instructions}` : '',
    ``,
    `Please confirm acceptance. Thank you.`,
    ``,
    `— NJK Logistics Pte Ltd`,
    `njklogisticsservices.com`,
    `+65 9836 6186`,
    `(${today})`,
  ].filter(Boolean);
  return lines.join('\n');
};

/**
 * Reusable supplier-dispatch dialog.
 * Props:
 *  - open, onOpenChange
 *  - prefilledJob   : object with job_date, pickup_location, etc. (auto-filled from DO/Invoice)
 *  - lockedSupplier : optional supplier obj (skips dropdown — used in SupplierManagement page)
 *  - sourceType     : 'delivery_order' | 'invoice' | 'manual'
 *  - sourceId, sourceNumber
 */
const DispatchSupplierDialog = ({
  open,
  onOpenChange,
  prefilledJob = {},
  lockedSupplier = null,
  sourceType = 'manual',
  sourceId = '',
  sourceNumber = '',
}) => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [job, setJob] = useState({ ...EMPTY_JOB, ...prefilledJob });

  useEffect(() => {
    if (!open) return;
    if (lockedSupplier) {
      setSupplierId(lockedSupplier.id);
      setSuppliers([lockedSupplier]);
    } else {
      axios.get(`${BACKEND_URL}/api/suppliers?status=active`)
        .then(res => setSuppliers(res.data || []))
        .catch(() => toast.error('Failed to load suppliers'));
    }
    setJob({ ...EMPTY_JOB, ...prefilledJob });
  }, [open, prefilledJob, lockedSupplier]);

  const selectedSupplier = suppliers.find(s => s.id === supplierId);

  // Auto-fill service from supplier's preferred service_type if job.service is empty
  useEffect(() => {
    if (selectedSupplier && !job.service && selectedSupplier.service_type) {
      setJob(j => ({ ...j, service: selectedSupplier.service_type }));
    }
  }, [supplierId]);

  const sendJobViaWhatsApp = async () => {
    if (!selectedSupplier) {
      toast.error('Please pick a supplier');
      return;
    }
    if (!selectedSupplier.phone) {
      toast.error('This supplier has no phone number. Edit supplier to add it.');
      return;
    }
    if (!job.job_date && !job.pickup_location && !job.cargo) {
      toast.error('Please fill at least Date, Pickup or Cargo before sending.');
      return;
    }
    const msg = buildJobMessage(selectedSupplier, job);

    // log dispatch (best-effort, don't block WhatsApp opening)
    axios.post(`${BACKEND_URL}/api/dispatch-logs`, {
      supplier_id: selectedSupplier.id,
      supplier_name: selectedSupplier.company_name,
      supplier_phone: selectedSupplier.phone,
      source_type: sourceType,
      source_id: sourceId,
      source_number: sourceNumber,
      ...job,
      message: msg,
    }).catch(() => { /* non-fatal */ });

    let phone = (selectedSupplier.phone || '').replace(/\D/g, '');
    if (phone.length === 8) phone = '65' + phone;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
    onOpenChange(false);
    toast.success(`Job sent to ${selectedSupplier.company_name} via WhatsApp`);
  };

  const supplierForPreview = selectedSupplier || lockedSupplier || { company_name: 'Supplier' };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle size={20} className="text-emerald-600" />
            Dispatch Job to Supplier via WhatsApp
          </DialogTitle>
          {sourceNumber && (
            <p className="text-xs text-gray-500 mt-1">
              Source: {sourceType === 'delivery_order' ? 'Delivery Order' : sourceType === 'invoice' ? 'Invoice' : 'Manual'} <span className="font-mono">{sourceNumber}</span>
            </p>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          {!lockedSupplier && (
            <div className="md:col-span-2">
              <Label>Supplier *</Label>
              {suppliers.length === 0 ? (
                <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded p-3">
                  <Building2 size={14} className="inline mr-1" />
                  No suppliers yet.{' '}
                  <Link to="/admin/suppliers" className="text-blue-600 underline">
                    Add suppliers first
                  </Link>.
                </div>
              ) : (
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger data-testid="dispatch-supplier-select">
                    <SelectValue placeholder="Select supplier..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.company_name}{s.service_type ? ` — ${s.service_type}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {lockedSupplier && (
            <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm">
                <span className="font-semibold">{lockedSupplier.company_name}</span>
                {lockedSupplier.contact_person && ` · ${lockedSupplier.contact_person}`}
                {' · '}
                <span className="text-emerald-700">{lockedSupplier.phone}</span>
              </p>
            </div>
          )}

          <div>
            <Label>Job Date</Label>
            <Input type="date" value={job.job_date}
                   onChange={(e) => setJob({ ...job, job_date: e.target.value })}
                   data-testid="job-date" />
          </div>
          <div>
            <Label>Time (optional)</Label>
            <Input type="time" value={job.job_time}
                   onChange={(e) => setJob({ ...job, job_time: e.target.value })}
                   data-testid="job-time" />
          </div>

          <div className="md:col-span-2">
            <Label>Service</Label>
            <Select value={job.service}
                    onValueChange={(v) => setJob({ ...job, service: v })}>
              <SelectTrigger data-testid="job-service"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Pickup Location</Label>
            <Input value={job.pickup_location}
                   onChange={(e) => setJob({ ...job, pickup_location: e.target.value })}
                   placeholder="e.g. Tuas South Ave 1"
                   data-testid="job-pickup" />
          </div>
          <div className="md:col-span-2">
            <Label>Drop-off Location</Label>
            <Input value={job.dropoff_location}
                   onChange={(e) => setJob({ ...job, dropoff_location: e.target.value })}
                   placeholder="e.g. Jurong Port"
                   data-testid="job-dropoff" />
          </div>

          <div>
            <Label>Cargo / Item</Label>
            <Input value={job.cargo}
                   onChange={(e) => setJob({ ...job, cargo: e.target.value })}
                   placeholder="e.g. Steel beams"
                   data-testid="job-cargo" />
          </div>
          <div>
            <Label>Weight / Quantity</Label>
            <Input value={job.weight}
                   onChange={(e) => setJob({ ...job, weight: e.target.value })}
                   placeholder="e.g. 5 tons / 10 units"
                   data-testid="job-weight" />
          </div>

          <div className="md:col-span-2">
            <Label>Customer / Reference</Label>
            <Input value={job.customer_ref}
                   onChange={(e) => setJob({ ...job, customer_ref: e.target.value })}
                   placeholder="e.g. ABC Construction / DO-2026-045"
                   data-testid="job-ref" />
          </div>

          <div className="md:col-span-2">
            <Label>Special Instructions</Label>
            <Textarea value={job.instructions}
                      onChange={(e) => setJob({ ...job, instructions: e.target.value })}
                      placeholder="Site contact, safety notes, equipment requirements..."
                      rows={3}
                      data-testid="job-instructions" />
          </div>

          <div className="md:col-span-2 mt-2">
            <Label className="text-emerald-700">WhatsApp Message Preview</Label>
            <pre className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-gray-800 whitespace-pre-wrap font-sans max-h-60 overflow-y-auto" data-testid="job-preview">
              {buildJobMessage(supplierForPreview, job)}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="job-cancel-btn">Cancel</Button>
          <Button onClick={sendJobViaWhatsApp}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="job-send-btn">
            <Send size={16} className="mr-2" /> Open in WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DispatchSupplierDialog;
